import React, { useState } from 'react';
import { ChevronDown, Download, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { presupuestoProformaService } from '../services/presupuestoProformaService';
import { PARTES } from '../constants/presupuestos';
import type { ParteExportacion, ParteItem, PdfExportado } from '../types';

interface BotonExportarProps {
  presupuestoId: string;
  nombrePresupuesto: string;
  disabled?: boolean;
  /** Tooltip del botón, por ejemplo para explicar por qué está deshabilitado. */
  title?: string;
  /**
   * Descarga alternativa. Por defecto se exporta el presupuesto independiente
   * `presupuestoId`.
   */
  exportar?: (parte: ParteItem) => Promise<PdfExportado>;
  /** Se ejecuta antes de exportar, para no dejar cambios sin guardar. */
  onAntesDeExportar?: () => Promise<void>;
  /** Pide confirmación antes de exportar esas partes; false cancela. */
  confirmarExportacion?: (partes: ParteItem[]) => Promise<boolean>;
}

/** Espera entre descargas: Chrome descarta la segunda si llega enseguida. */
const PAUSA_ENTRE_DESCARGAS_MS = 300;

const esperar = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const guardarArchivo = (blob: Blob, nombreArchivo: string) => {
  const url = window.URL.createObjectURL(blob);
  const enlace = document.createElement('a');

  enlace.href = url;
  enlace.download = nombreArchivo;
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
  // Revocar enseguida puede cortar la descarga antes de que empiece.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

/**
 * Exporta el presupuesto: un PDF por parte. "Exportar ambos" (la opción
 * principal) descarga los dos PDFs por separado.
 */
export const BotonExportar: React.FC<BotonExportarProps> = ({
  presupuestoId,
  nombrePresupuesto,
  disabled = false,
  title,
  exportar: descargar,
  onAntesDeExportar,
  confirmarExportacion,
}) => {
  const [exportando, setExportando] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);

  const exportar = async (parte: ParteExportacion) => {
    const partes: ParteItem[] = parte === 'ambos' ? PARTES : [parte];

    if (confirmarExportacion && !(await confirmarExportacion(partes))) return;

    setExportando(true);

    try {
      await onAntesDeExportar?.();

      for (const [indice, parteActual] of partes.entries()) {
        if (indice > 0) await esperar(PAUSA_ENTRE_DESCARGAS_MS);

        const { blob, nombreArchivo } = descargar
          ? await descargar(parteActual)
          : await presupuestoProformaService.exportar(presupuestoId, parteActual);

        guardarArchivo(blob, nombreArchivo ?? `Presupuesto - ${parteActual.toUpperCase()}.pdf`);
      }
    } catch {
      toast.error(`No se pudo exportar "${nombrePresupuesto}". Intente nuevamente.`);
    } finally {
      setExportando(false);
    }
  };

  // Cerrar el menú y diferir la exportación: puede abrir la confirmación de
  // ítems sin destino, y si un Dialog (Radix) se abre en el mismo tick en que
  // se cierra el DropdownMenu, ambos pisan `pointer-events` de <body> y la
  // página queda sin poder tocarse.
  const exportarDesdeMenu = (parte: ParteExportacion) => {
    setMenuAbierto(false);
    setTimeout(() => void exportar(parte), 100);
  };

  return (
    <div className="flex" title={title}>
      <Button
        onClick={() => void exportar('ambos')}
        disabled={disabled || exportando}
        className="rounded-r-none"
      >
        {exportando ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Download className="mr-2 h-4 w-4" />
        )}
        Exportar presupuesto
      </Button>

      <DropdownMenu open={menuAbierto} onOpenChange={setMenuAbierto}>
        <DropdownMenuTrigger asChild>
          <Button
            disabled={disabled || exportando}
            className="rounded-l-none border-l border-primary-foreground/20 px-2"
            aria-label="Opciones de exportación"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => exportarDesdeMenu('ambos')}>
            Exportar ambos
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => exportarDesdeMenu('adquiriente')}>
            Exportar adquiriente
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => exportarDesdeMenu('transferente')}>
            Exportar transferente
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
